/**
 * Account Cases tab - table-based list with filters and Add case
 */
import React, { useState, useEffect, useCallback } from 'react';
import {
  Box,
  Typography,
  Button,
  TextField,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TablePagination,
  Chip,
  IconButton,
  CircularProgress,
  Alert,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  InputAdornment,
} from '@mui/material';
import {
  Add as AddIcon,
  Search as SearchIcon,
  Visibility as ViewIcon,
  Clear as ClearIcon,
} from '@mui/icons-material';
import { Case } from '../types/case';
import apiService from '../services/api';
import CaseFormDialog from './CaseFormDialog';

const STATUS_LABELS: Record<string, string> = {
  new: 'New',
  in_progress: 'In Progress',
  end_user_reply: 'End User Reply',
  awaiting_end_user: 'Awaiting End User',
  awaiting_upstream_carrier: 'Awaiting Upstream',
  awaiting_nbn_appointment: 'Awaiting NBN',
  escalated: 'Escalated',
  on_hold: 'On Hold',
  billing_system_update_required: 'Billing Update',
  completed: 'Completed',
};

interface AccountCasesTabProps {
  accountId: string;
  navigate: (path: string) => void;
}

const AccountCasesTab: React.FC<AccountCasesTabProps> = ({ accountId, navigate }) => {
  const [cases, setCases] = useState<Case[]>([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(25);
  const [filters, setFilters] = useState<Record<string, string>>({
    status: '',
    case_type: '',
    assignee_id: '',
    q: '',
  });
  const [users, setUsers] = useState<{ id: string; username: string; display_name: string }[]>([]);
  const [formOpen, setFormOpen] = useState(false);

  const fetchCases = useCallback(async () => {
    if (!accountId) return;
    try {
      setLoading(true);
      setError(null);
      const params: Record<string, any> = {
        page: page + 1,
        per_page: rowsPerPage,
        sort_by: 'created_at',
        sort_order: 'desc',
      };
      if (filters.q) params.q = filters.q;
      if (filters.status) params.status = filters.status;
      if (filters.case_type) params.case_type = filters.case_type;
      if (filters.assignee_id) params.assignee_id = filters.assignee_id;
      const res = await apiService.getCasesByAccount(accountId, params);
      setCases(res.cases);
      setTotal(res.total);
    } catch (e: any) {
      setError(e?.response?.data?.detail || 'Failed to load cases');
    } finally {
      setLoading(false);
    }
  }, [accountId, page, rowsPerPage, filters]);

  useEffect(() => {
    fetchCases();
  }, [fetchCases]);

  useEffect(() => {
    apiService.getUsers().then(setUsers).catch(() => {});
  }, []);

  const handleClearFilters = () => {
    setFilters({ status: '', case_type: '', assignee_id: '', q: '' });
    setPage(0);
  };

  const handleCaseCreated = () => {
    setFormOpen(false);
    fetchCases();
  };

  const formatDate = (s: string | null | undefined) => {
    if (!s) return '-';
    return new Date(s).toLocaleDateString('en-AU', {
      day: 'numeric',
      month: 'short',
      year: 'numeric',
    });
  };

  if (!accountId) return null;

  return (
    <Paper variant="outlined" sx={{ p: 3 }}>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
        <Typography variant="h6" color="primary.main">
          Cases
        </Typography>
        <Button
          variant="contained"
          size="small"
          startIcon={<AddIcon />}
          onClick={() => setFormOpen(true)}
        >
          Add case
        </Button>
      </Box>

      <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 2, alignItems: 'center', mb: 2 }}>
        <TextField
          size="small"
          placeholder="Search subject, case number..."
          value={filters.q}
          onChange={(e) => setFilters({ ...filters, q: e.target.value })}
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                <SearchIcon />
              </InputAdornment>
            ),
          }}
          sx={{ minWidth: 180 }}
        />
        <FormControl size="small" sx={{ minWidth: 120 }}>
          <InputLabel>Status</InputLabel>
          <Select
            value={filters.status}
            label="Status"
            onChange={(e) => { setFilters({ ...filters, status: e.target.value }); setPage(0); }}
          >
            <MenuItem value="">All</MenuItem>
            {Object.entries(STATUS_LABELS).map(([k, v]) => (
              <MenuItem key={k} value={k}>{v}</MenuItem>
            ))}
          </Select>
        </FormControl>
        <FormControl size="small" sx={{ minWidth: 100 }}>
          <InputLabel>Type</InputLabel>
          <Select
            value={filters.case_type}
            label="Type"
            onChange={(e) => { setFilters({ ...filters, case_type: e.target.value }); setPage(0); }}
          >
            <MenuItem value="">All</MenuItem>
            <MenuItem value="service">Service</MenuItem>
            <MenuItem value="billing">Billing</MenuItem>
            <MenuItem value="other">Other</MenuItem>
          </Select>
        </FormControl>
        <FormControl size="small" sx={{ minWidth: 120 }}>
          <InputLabel>Assignee</InputLabel>
          <Select
            value={filters.assignee_id}
            label="Assignee"
            onChange={(e) => { setFilters({ ...filters, assignee_id: e.target.value }); setPage(0); }}
          >
            <MenuItem value="">All</MenuItem>
            {users.map((u) => (
              <MenuItem key={u.id} value={u.id}>{u.display_name || u.username}</MenuItem>
            ))}
          </Select>
        </FormControl>
        <Button size="small" startIcon={<ClearIcon />} onClick={handleClearFilters}>
          Clear
        </Button>
        <Button variant="outlined" size="small" onClick={fetchCases} disabled={loading}>
          Apply
        </Button>
      </Box>

      {error && (
        <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>
          {error}
        </Alert>
      )}

      {loading ? (
        <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
          <CircularProgress size={32} />
        </Box>
      ) : (
        <>
          <TableContainer>
            <Table size="small" stickyHeader>
              <TableHead>
                <TableRow>
                  <TableCell sx={{ fontWeight: 600 }}>Case #</TableCell>
                  <TableCell sx={{ fontWeight: 600 }}>Subject</TableCell>
                  <TableCell sx={{ fontWeight: 600 }}>Status</TableCell>
                  <TableCell sx={{ fontWeight: 600 }}>Priority</TableCell>
                  <TableCell sx={{ fontWeight: 600 }}>Assignee</TableCell>
                  <TableCell sx={{ fontWeight: 600 }}>Created</TableCell>
                  <TableCell sx={{ fontWeight: 600 }} align="right">Actions</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {cases.length === 0 ? (
                  <TableRow>
                    <TableCell colSpan={7} align="center" sx={{ py: 4 }}>
                      No cases for this account
                    </TableCell>
                  </TableRow>
                ) : (
                  cases.map((c) => (
                    <TableRow key={c.id} hover>
                      <TableCell>{c.case_number}</TableCell>
                      <TableCell>{c.subject}</TableCell>
                      <TableCell>
                        <Chip
                          label={STATUS_LABELS[c.status] || c.status}
                          size="small"
                          color={c.status === 'completed' ? 'success' : 'default'}
                        />
                      </TableCell>
                      <TableCell>{c.priority}</TableCell>
                      <TableCell>{c.assignee_name || '-'}</TableCell>
                      <TableCell>{formatDate(c.created_at)}</TableCell>
                      <TableCell align="right">
                        <IconButton size="small" onClick={() => navigate(`/crm/cases/${c.id}`)} title="View case">
                          <ViewIcon fontSize="small" />
                        </IconButton>
                      </TableCell>
                    </TableRow>
                  ))
                )}
              </TableBody>
            </Table>
          </TableContainer>
          <TablePagination
            component="div"
            count={total}
            page={page}
            onPageChange={(_, p) => setPage(p)}
            rowsPerPage={rowsPerPage}
            onRowsPerPageChange={(e) => {
              setRowsPerPage(parseInt(e.target.value, 10));
              setPage(0);
            }}
            rowsPerPageOptions={[10, 25, 50]}
          />
        </>
      )}

      <CaseFormDialog
        open={formOpen}
        onClose={() => setFormOpen(false)}
        onSuccess={handleCaseCreated}
        accountId={accountId}
      />
    </Paper>
  );
};

export default AccountCasesTab;
